import { Button } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text } from '~/components/core'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import {
  growingBannerStyles,
  growingBannerTextRowStyles,
} from '~/pages/Home/Banners/utils/growingBannerStyles'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'

interface WrapperProps {
  $show: boolean
}

// Warning-colored (not the green "update ready" banner): this is an action the
// user/IT needs to take, not good news.
const Wrapper = styled.div<WrapperProps>`
  ${growingBannerStyles()}
  padding-inline: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
  background-color: var(--chakra-colors-orange-solid);
  align-items: center;
`

const TextWrapper = styled.div`
  ${growingBannerTextRowStyles}
`

// Persistent notice shown when the desktop app repeatedly fails to apply an
// update because something on the machine (typically antivirus/EDR) is blocking
// the staged copy. The user can't resolve that from inside the app, so we
// surface it plainly and offer a manual reinstall — which uses a different,
// un-blocked install path (Gatekeeper + file copy, not the in-place ShipIt swap).
// Intentionally NOT dismissible: the machine is stranded on an old version and
// silently hiding it is how it went unnoticed in the first place.
const UpdateBlockedBanner = ({ onShowCallback }: BannerProps) => {
  const { t } = useTranslation()
  const { isUpdateBlocked, updateBlockedDownloadUrl } =
    useElectronUpdaterContext()

  const show = isElectron && isUpdateBlocked

  useEffect(() => {
    onShowCallback(show)
  }, [show, onShowCallback])

  const handleDownload = () => {
    if (updateBlockedDownloadUrl) {
      openInNewTab(updateBlockedDownloadUrl)
    }
  }

  return (
    <Wrapper $show={show}>
      <TextWrapper>
        <Text color="var(--chakra-colors-orange-contrast)">
          {t(
            'updateBlockedBanner.message',
            "The app couldn't finish updating. This is usually caused by antivirus or security software blocking the update. Ask your IT to allow the app to update, or download the latest version.",
          )}
        </Text>
        {updateBlockedDownloadUrl && (
          <Button
            onClick={handleDownload}
            bg="bg.panel"
            color="fg.default"
            borderRadius="full"
            _hover={{ bg: 'bg.muted' }}
            size="sm"
          >
            {t('updateBlockedBanner.downloadLatest', 'Download latest version')}
          </Button>
        )}
      </TextWrapper>
    </Wrapper>
  )
}

export default UpdateBlockedBanner
